import React from 'react';

/**
 * Highlights exact penetration phrases and OMNI impact text in a comment string with HTML spans.
 * - Highlights Swedish and English penetration phrases with red/bold styling
 * - Highlights OMNI "impact" text (marked with ~~IMPACT~~) with red/bold styling
 * 
 * @param {string} comment - The comment text to process
 * @param {Function} t - Translation function to get localized penetration phrase
 * @returns {React.ReactElement} React fragment with highlighted phrases
 */
export function highlightPenetrationPhrases(comment, t) {
  if (!comment || typeof comment !== 'string') {
    return comment;
  }

  // First, handle OMNI impact markers (~~IMPACT~~text~~IMPACTEND~~) and
  // no-impact markers (~~NOIMPACT~~text~~NOIMPACTEND~~)
  const markerRegex = /~~(IMPACT|NOIMPACT)~~(.*?)~~(?:IMPACTEND|NOIMPACTEND)~~/g;
  let parts = [];
  let lastIndex = 0;
  let match;
  
  // Split by impact/no-impact markers first
  while ((match = markerRegex.exec(comment)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push({ text: comment.substring(lastIndex, match.index), isImpact: false });
    }
    // Add the matched text with the appropriate flag
    if (match[1] === 'NOIMPACT') {
      parts.push({ text: match[2], isNoImpact: true });
    } else {
      parts.push({ text: match[2], isImpact: true });
    }
    lastIndex = markerRegex.lastIndex;
  }
  // Add remaining text
  if (lastIndex < comment.length) {
    parts.push({ text: comment.substring(lastIndex), isImpact: false });
  }
  
  // If no markers found, treat entire comment as one part
  if (parts.length === 0) {
    parts.push({ text: comment, isImpact: false });
  }

  // Now process each part for penetration phrases
  const finalParts = [];
  parts.forEach((part, partIndex) => {
    if (part.isImpact) {
      // Impact text - don't process for penetration phrases
      finalParts.push({ text: part.text, isImpact: true, key: `impact-${partIndex}` });
    } else if (part.isNoImpact) {
      // No-impact text - don't process for penetration phrases
      finalParts.push({ text: part.text, isNoImpact: true, key: `noimpact-${partIndex}` });
    } else {
      // Process for penetration phrases
      const penetrationPhrase = t ? t('commentPenetrates') : 'Objektet penetrerar ytan';
      const escapedPhrase = penetrationPhrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(${escapedPhrase}\\.?)`, 'g');
      
      const subParts = part.text.split(regex);
      subParts.forEach((subPart, subIndex) => {
        const phraseRegex = new RegExp(`^${escapedPhrase}\\.?$`);
        if (phraseRegex.test(subPart)) {
          finalParts.push({ text: subPart, isPenetration: true, key: `pen-${partIndex}-${subIndex}` });
        } else if (subPart) {
          finalParts.push({ text: subPart, isNormal: true, key: `norm-${partIndex}-${subIndex}` });
        }
      });
    }
  });
  
  // Build React elements
  return React.createElement(React.Fragment, null,
    ...finalParts.map((part) => {
      if (part.isImpact) {
        return React.createElement('span', {
          key: part.key,
          className: 'omni-impact-flag'
        }, part.text);
      } else if (part.isNoImpact) {
        return React.createElement('span', {
          key: part.key,
          className: 'omni-no-impact-flag'
        }, part.text);
      } else if (part.isPenetration) {
        return React.createElement('span', {
          key: part.key,
          className: 'penetration-flag'
        }, part.text);
      } else {
        return part.text;
      }
    })
  );
}

/**
 * Strips penetration styling markup from text for PDF export.
 * Removes <span class="penetration-flag"> wrapper tags while preserving the text content.
 * ~~IMPACT~~ markers are intentionally kept intact so the PDF renderer can apply
 * red+bold styling to critical impact text via willDrawCell/didDrawCell.
 * 
 * @param {string} text - Text that may contain HTML span markup or ~~IMPACT~~ markers
 * @returns {string} Text with HTML span tags removed; ~~IMPACT~~ markers preserved
 */
export function stripPenetrationStylingForPDF(text) {
  if (!text || typeof text !== 'string') {
    return text || '';
  }
  
  // Remove penetration flag span tags while preserving content
  const cleaned = text.replace(/<span[^>]*class="penetration-flag"[^>]*>(.*?)<\/span>/g, '$1');
  
  // NOTE: ~~IMPACT~~ and ~~NOIMPACT~~ markers are intentionally preserved here so the PDF
  // renderer's willDrawCell/didDrawCell handlers can apply red/green bold styling.
  
  return cleaned;
}